import type { Edges, MapNode, NodeId, NodeMap } from './types';

/**
 * The node popup's AI-documentation fields: the tri-state grounding overrides and the
 * live-system inheritance walk.
 *
 * Pure, and here rather than in the component, because both halves of it are arithmetic over
 * the map that is easy to get subtly wrong and impossible to see going wrong: a tri-state
 * that round-trips through a `<select>` (whose values are always strings), and an ancestor
 * walk that has to terminate on a map somebody has managed to make cyclic.
 *
 * THE VANILLA SIDE KEEPS THE KEYS, NOT THE LABELS. `flushDirtyAiFields()` and the popup's undo
 * entry both iterate these four field names, so `infostation.html` still holds
 * `CONTEXT_SOURCE_KEYS`. What it no longer holds is what each one is CALLED, because nothing
 * there renders them any more — that is this file's, and there is one copy of it.
 */

/** The four grounding sources a node can override, in the order the popup lists them. */
export const CONTEXT_SOURCES = [
  { key: 'useExistingDoc', label: "This node's existing documentation" },
  { key: 'useNeighborDocs', label: 'Neighboring nodes (parent, siblings, children)' },
  { key: 'useSemanticGrounding', label: 'Semantically related documentation' },
  { key: 'useLinkedDocs', label: "Linked nodes' documentation" },
] as const satisfies readonly { key: keyof MapNode; label: string }[];

export type ContextSourceKey = (typeof CONTEXT_SOURCES)[number]['key'];

/** What the suite-wide config says, for labelling each "Inherit" option with its outcome. */
export type GroundingDefaults = Partial<Record<ContextSourceKey, boolean>>;

/** A source is on unless the config says otherwise — the server's own default, mirrored. */
export const inheritsAs = (cfg: GroundingDefaults | null | undefined, key: ContextSourceKey): 'on' | 'off' =>
  (cfg ?? {})[key] !== false ? 'on' : 'off';

/**
 * `null` <-> `''`.
 *
 * `<select>` values are strings, so the "inherit" option needs a string that cannot collide
 * with a real `true`/`false`. The empty string is that value, and it round-trips through
 * `ctxTriParse` back to `null`.
 */
export const ctxTriValue = (v: boolean | null | undefined): string =>
  v === true ? 'true' : v === false ? 'false' : '';

export const ctxTriParse = (s: string): boolean | null =>
  s === 'true' ? true : s === 'false' ? false : null;

/** The tri-state overrides as the popup edits them: every key present, `null` for inherit. */
export type ContextOverrides = Record<ContextSourceKey, boolean | null>;

export function contextOverridesOf(n: MapNode | null | undefined): ContextOverrides {
  const out = {} as ContextOverrides;
  for (const s of CONTEXT_SOURCES) out[s.key] = (n?.[s.key] ?? null) as boolean | null;
  return out;
}

/**
 * The nearest ancestor (excluding this node) that names a live target.
 *
 * What this node inherits when it does not name one itself. Mirrors the server's
 * `resolveAppTarget()` FOR DISPLAY ONLY — the server stays the authority when a run actually
 * happens, and duplicating it here is about telling somebody what will happen, not deciding it.
 *
 * The `guard` set is not defensive decoration: `edges` is child -> parent and a reparent that
 * went wrong could close a loop, and an ancestor walk that meets one never returns.
 */
export function inheritedAppTarget(
  id: NodeId,
  nodes: NodeMap,
  edges: Edges,
): { targetId: string; fromId: NodeId } | null {
  let cur: NodeId | undefined = edges[id];
  const guard = new Set<NodeId>([id]);
  while (cur !== undefined && !guard.has(cur)) {
    guard.add(cur);
    const n = nodes[cur];
    if (n && n.appTargetId) return { targetId: n.appTargetId, fromId: cur };
    cur = edges[cur];
  }
  return null;
}
